if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.sliderInstance = false;
        this.thumbsInstance = false;
      }

      connectedCallback() {
        this.init();
      }

      init() {
        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.variantShowcase = this.dataset.variantShowcase === 'true';
        this.hideVariantMedia = this.dataset.hideVariantMedia === 'true';
        if (this.variantShowcase) {
          this.showcaseSizes = this.dataset.showcaseSizes || '';
          // The data/lookup <script>s render at the END of this element. On the initial HTML
          // parse the custom element can upgrade (connectedCallback fire) BEFORE those children
          // exist, so reading them here once — and caching {} — would permanently break variant
          // switching until a hard reload (the "works only after a refresh" bug). Parse lazily
          // instead (retries until present); by the first interaction the DOM is fully parsed.
          this.ensureShowcaseData();
          if (this.currentShowcaseId === undefined) {
            this.currentShowcaseId = this.dataset.selectedVariant;
            const liveFirst = this.querySelector('[id^="Slider-Gallery"] .product__media-item');
            this._currentFirstU = liveFirst ? liveFirst.dataset.src : null;
          }
          // Update the gallery instantly on option change, from client-side data,
          // without waiting for the section fetch (which the theme also runs and
          // which calls setActiveMedia again — the skip guard avoids a double build).
          if (!this._showcaseListenerBound) {
            this._showcaseListenerBound = true;
            this._showcaseScope = this.closest('product-info') || document;
            this._showcaseScope.addEventListener('change', (e) => {
              if (!e.target || !e.target.matches('input, select')) return;
              this.applyShowcaseFromSelection();
            });
            // Preload the first (gallery) image of a variant when its swatch is
            // hovered, so the actual switch has nothing left to download.
            this._showcaseScope.addEventListener('pointerover', (e) => {
              const label = e.target.closest && e.target.closest('label[for]');
              const input = label
                ? this._showcaseScope.querySelector('#' + CSS.escape(label.getAttribute('for')))
                : e.target.closest && e.target.closest('input[type="radio"]');
              if (input) this.preloadShowcaseForControl(input);
            });
          }
        }
        this.context = this.dataset.context;
        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.initImageZoom();
        }
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);
        const mediaItemGapMobile = parseInt(this.dataset.mediaItemGapMobile);

        this.sliderOptions = {
          init: false,
          slidesPerView: this.enableMobileThumbnails ? 1 : 'auto',
          spaceBetween: mediaItemGapMobile,
          loop: true,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
              direction: 'horizontal',
            },
          },
          spaceBetween: mediaItemGapMobile,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
        };

        if (this.variantShowcase) {
          // No transition animation on variant showcase galleries.
          this.sliderOptions.speed = 0;
          this.thumbsOptions.speed = 0;
        }

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              768: {
                direction: 'vertical',
                slidesPerView: 'auto',
                spaceBetween: mediaItemGap,
              },
            };
            break;
          case 'carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              1024: {
                slidesPerView: 5,
              },
              1536: {
                slidesPerView: 7,
              },
            };
            break;
        }

        if (this.variantShowcase) {
          // Keep the thumbnails' Swiper slidesPerView aligned with the CSS
          // `--slides-view: 5` (which sizes the slides before Swiper initialises).
          // Otherwise the few thumbnails visibly resize on init (e.g. 1/5 -> 1/7
          // on wide screens, or 1/5 -> 1/4 on mobile).
          this.thumbsOptions.slidesPerView = 5;
          this.thumbsOptions.breakpoints = {
            768: { spaceBetween: mediaItemGap, slidesPerView: 5 },
          };
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      buildZoomDataSource() {
        let dataSource = [];
        const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        if (allMedia) {
          allMedia.forEach((media) => {
            switch (media.dataset.mediaType) {
              case 'model':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('product-model').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'video':
              case 'external_video':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('video-element').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'image':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  src: media.dataset.src,
                  width: media.dataset.pswpWidth,
                  height: media.dataset.pswpHeight,
                  mediaType: media.dataset.mediaType,
                });
                break;
            }
          });
        }
        return dataSource;
      }

      refreshZoomDataSource() {
        if (this.lightbox) {
          this.lightbox.options.dataSource = this.buildZoomDataSource();
        }
      }

      initImageZoom() {
        // Bind once; on re-init (e.g. variant change) just refresh the data source
        // so we don't stack duplicate lightboxes / click delegates.
        if (this._zoomInited) {
          this.refreshZoomDataSource();
          return;
        }
        this._zoomInited = true;

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: this.buildZoomDataSource(),
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el;
              }
            }
          }

          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data: { id } }) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el.src;
              }
            }
          }

          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { currIndex } = this.lightbox.pswp;
            if (this.sliderInstance.slider.realIndex !== currIndex) {
              this.sliderInstance.slider.slideToLoop(currIndex, 100, false);
            }
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox.pswp.currSlide.data.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.next();
              },
            });
            this.lightbox.pswp.ui.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.prev();
              },
            });
          }
          this.lightbox.pswp.ui.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox.pswp.close();
            },
          });
        });

        this.lightbox.init();

        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset.mediaType === 'image') {
              const index = Number(media.dataset.mediaIndex) || 0;
              this.lightbox.loadAndOpen(index);
            }
          },
        });
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;

          if (thumbs.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (this.hideVariantMedia) return;

        if (this.variantShowcase) {
          this.setActiveShowcase(variant);
          return;
        }

        if (!variant || !variant.hasOwnProperty('featured_media') || !variant.featured_media) return;

        if (this.sliderInstance.slider) {
          const slideIndex = variant.featured_media.position || 0;
          this.sliderInstance.slider.slideToLoop(slideIndex - 1);
        } else {
          this.sortMediaItems(variant);
        }
      }

      ensureShowcaseData() {
        if (this._showcaseReady) return true;
        const dataEl = this.querySelector('script[data-variant-showcase-data]');
        const lookupEl = this.querySelector('script[data-variant-showcase-lookup]');
        if (!dataEl || !lookupEl) return false;
        try {
          this.showcaseData = JSON.parse(dataEl.textContent);
        } catch (e) {
          this.showcaseData = {};
        }
        try {
          this.showcaseLookup = JSON.parse(lookupEl.textContent);
        } catch (e) {
          this.showcaseLookup = {};
        }
        this._showcaseReady = true;
        return true;
      }

      preloadShowcaseForControl(input) {
        if (!this.ensureShowcaseData()) return;
        if (this._lastHoverInput === input) return; // avoid rework on every pointer move
        this._lastHoverInput = input;

        const hoveredValue = (input.value || '').trim();
        if (!hoveredValue) return;

        // Target selection = current choices, but with this control's option group
        // swapped to the hovered value.
        const scope = this._showcaseScope || document;
        const picker = scope.querySelector('variant-picker') || scope;
        const valueSet = new Set();
        picker.querySelectorAll('input[type="radio"]:checked, select').forEach((c) => {
          if (c.name !== input.name) valueSet.add((c.value || '').trim());
        });
        valueSet.add(hoveredValue);

        let matchId = null;
        let matchLen = -1;
        for (const key in this.showcaseLookup) {
          const parts = key.split('~');
          if (parts.length > matchLen && parts.every((p) => valueSet.has(p))) {
            matchLen = parts.length;
            matchId = this.showcaseLookup[key];
          }
        }
        if (!matchId) return;

        this._preloadedShowcase = this._preloadedShowcase || new Set();
        if (this._preloadedShowcase.has(matchId)) return;
        const imgs = this.showcaseData[matchId];
        if (!imgs || !imgs.length) return;
        this._preloadedShowcase.add(matchId);
        const pre = new Image();
        pre.src = this.showcaseUrlWidth(imgs[0].u, 980);
      }

      applyShowcaseFromSelection() {
        if (!this.ensureShowcaseData()) return;
        const scope = this._showcaseScope || this.closest('product-info') || document;
        const picker = scope.querySelector('variant-picker') || scope;
        const controls = picker.querySelectorAll('input[type="radio"]:checked, select');
        const valueSet = new Set([...controls].map((c) => (c.value || '').trim()).filter(Boolean));
        if (!valueSet.size) return;

        // The picker can also contain unrelated controls (quantity, a duplicate
        // picker injected by the section fetch), so match by subset: find the
        // variant whose option values are all currently selected. Prefer the most
        // specific (longest) combo.
        let matchId = null;
        let matchLen = -1;
        for (const key in this.showcaseLookup) {
          const parts = key.split('~');
          if (parts.length > matchLen && parts.every((p) => valueSet.has(p))) {
            matchLen = parts.length;
            matchId = this.showcaseLookup[key];
          }
        }
        if (matchId) this.setActiveShowcase({ id: matchId });
      }

      setActiveShowcase(variant) {
        if (!variant || !variant.id) return;
        this.ensureShowcaseData();

        const imgs = this.showcaseData && this.showcaseData[variant.id];
        if (!imgs || !imgs.length) return; // No data for this variant — keep current gallery.

        // Skip rebuild when the resolved images are identical (e.g. same colour,
        // different size with no distinct variant image) — avoids DOM churn / re-fetch.
        this.currentShowcaseId = variant.id;
        if (imgs[0] && imgs[0].u === this._currentFirstU) return;
        this._currentFirstU = imgs[0] ? imgs[0].u : null;

        // Warm the cache for the first image (non-blocking), then swap synchronously
        // so the switch stays instant. The active slide has no `is="image-lazy"`, so
        // there's no opacity fade-in (white flash) — at most a brief faint placeholder
        // tint while the image streams in (negligible on production's CDN).
        if (this._currentFirstU) {
          const pre = new Image();
          pre.src = this.showcaseUrlWidth(this._currentFirstU, 980);
        }
        const slidesHTML = imgs.map((img, i) => this.buildShowcaseSlide(img, i)).join('');
        const thumbsHTML = imgs.map((img, i) => this.buildShowcaseThumb(img, i)).join('');
        this.rebuildShowcase(slidesHTML, thumbsHTML);
      }

      showcaseUrlWidth(url, w) {
        return url.replace(/([?&])width=\d+/, '$1width=' + w);
      }

      escapeAttr(s) {
        return String(s || '')
          .replace(/&/g, '&amp;')
          .replace(/"/g, '&quot;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');
      }

      buildShowcaseSlide(img, i) {
        const eager = i === 0;
        const ar = img.h ? (img.w / img.h).toFixed(4) : 1;
        const alt = this.escapeAttr(img.a);
        const widths = [400, 620, 810, 980, 1240, 1620, 1946];
        const srcset = widths.map((w) => `${this.showcaseUrlWidth(img.u, w)} ${w}w`).join(', ');
        const displaySrc = this.showcaseUrlWidth(img.u, 980);
        const zoom = this.enableImageZoom
          ? `<div class="product__media-icon btn btn--white btn--small btn--icon btn--icon-circle" aria-hidden="true"><span class="btn__text flex items-center"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7"></circle><path d="m21 21-4.3-4.3"></path><path d="M11 8v6M8 11h6"></path></svg></span></div>` +
            `<button class="product__media-zoom btn--inherit js-photoswipe--zoom" type="button" aria-haspopup="dialog" data-media-id="showcase-${i}" data-media-type="image" data-media-index="${i}"><span class="visually-hidden"></span></button>`
          : '';
        // Active slide has no `is="image-lazy"` so it paints immediately without
        // the opacity fade-in (which reads as a white flash on variant change).
        const imgTag = eager
          ? `<img src="${displaySrc}" srcset="${srcset}" sizes="${this.showcaseSizes}" width="${img.w}" height="${img.h}" loading="eager" fetchpriority="high" alt="${alt}">`
          : `<img is="image-lazy" src="${displaySrc}" srcset="${srcset}" sizes="${this.showcaseSizes}" width="${img.w}" height="${img.h}" loading="lazy" alt="${alt}">`;
        return (
          `<div tabindex="-1" class="product__media-item swiper-slide" data-media-id="showcase-${i}" data-media-type="image" data-media-index="${i}" data-src="${img.u}" data-pswp-width="${img.w}" data-pswp-height="${img.h}">` +
          `<div class="product__media-item-container relative flex overflow-hidden blocks-radius media-type-image" style="--aspect-ratio: ${ar};">` +
          `<div class="block product__media media-wrapper">${imgTag}</div>${zoom}</div></div>`
        );
      }

      buildShowcaseThumb(img, i) {
        const alt = this.escapeAttr(img.a);
        const widths = [160, 200, 240, 300, 400];
        const srcset = widths.map((w) => `${this.showcaseUrlWidth(img.u, w)} ${w}w`).join(', ');
        const src = this.showcaseUrlWidth(img.u, 320);
        // No image-lazy (it rewrote sizes tiny -> blurry). Fixed sizes so 2x picks ~300px.
        return (
          `<div class="product__thumbs-item swiper-slide" data-target="showcase-${i}" data-media-position="${i + 1}">` +
          `<div class="product__thumbs-container relative blocks-radius-md media-wrapper cursor-pointer">` +
          `<div class="product__thumbs-inner" style="--aspect-ratio: 1">` +
          `<img src="${src}" srcset="${srcset}" sizes="(max-width: 767px) 18vw, 150px" width="320" height="320" loading="lazy" alt="${alt}">` +
          `</div></div></div>`
        );
      }

      rebuildShowcase(slidesHTML, thumbsHTML) {
        // Tear down existing swiper instances before swapping slides.
        if (typeof this.sliderInstance === 'object' && this.sliderInstance.slider) {
          this.sliderInstance.slider.destroy(true, true);
        }
        this.sliderInstance = false;
        if (typeof this.thumbsInstance === 'object' && this.thumbsInstance.slider) {
          this.thumbsInstance.slider.destroy(true, true);
        }
        this.thumbsInstance = false;

        // Swap in the new variant's slides (this triggers the image requests —
        // only for the active variant).
        this.elements.mediaList.innerHTML = slidesHTML;
        if (this.elements.thumbnails) {
          const thumbWrapper = this.elements.thumbnails.querySelector('.thumbnail-list');
          if (thumbWrapper) thumbWrapper.innerHTML = thumbsHTML;
        }

        // Re-query media items and re-init the slider for the current breakpoint.
        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.updateMediaLayout();

        // Refresh the zoom data source to point at the new slides.
        this.refreshZoomDataSource();
      }

      sortMediaItems(variant) {
        let newMedias = this.elements.mediaItems;

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == variant.featured_media.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        if (!FoxTheme.config.mqlMobile && this.context !== 'quickview') {
          const selectedMedia = this.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }
    }
  );
}
